import { InputHelp, FieldFooter } from "../controlGuidance.jsx";
import { useState } from "react";
import { NOTE_CLASS } from "../../../lib/ui.js";
import { setValueAt, valueAt } from "../catalogDocument.js";
import { ChoiceCard } from "../controls.jsx";
import { INPUT_CLASS, SECONDARY_ACTION } from "../styles.js";
import { GroupHeader, Interaction, OnEndpoints } from "../textRoles.jsx";
import {
    BOOL_DETAILS,
    CHOICE_DETAILS,
    COPILOT_COPY,
    POINTERS,
    SURFACE_COPY,
    SURFACE_LABELS,
    describeMarketplaceSource,
    describeMcpMatcher,
    interactions,
    onEndpoints,
    optionLabel,
    sourceKindLabel,
    surfaceOnEndpoints,
} from "./help.js";

const KIND = "MANAGED_AGENT_CONFIG_VALUE_KIND_";
const RULE = "MANAGED_AGENT_CONFIG_STRING_RULE_";
const inputClass = `${INPUT_CLASS} min-w-0`;
const buttonClass = SECONDARY_ACTION;
const removeClass = "border-0 bg-transparent text-[9px] text-[color:var(--muted)]";

// The login rule from the catalog: 1 to 39 letters, digits and single inner
// hyphens. The server checks it again on preview.
const GITHUB_LOGIN = /^(?=.{1,39}$)[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/;
const MCP_SERVER_NAME = /^[A-Za-z0-9_-]+$/;

const PLACEHOLDERS = Object.freeze({
    [POINTERS.loginOrgs]: "octocorp",
    [POINTERS.model]: "auto",
    [POINTERS.effort]: "high",
    [POINTERS.deny]: "shell(rm)",
    [POINTERS.ask]: "shell(git push)",
    [POINTERS.allow]: "shell(git status)",
    [POINTERS.limitTo]: "Domain(github.com)",
});

// Lists where an empty list is a deliberate choice, with the button that
// writes it.
const EMPTY_LIST_ACTIONS = Object.freeze({
    [POINTERS.limitTo]: "Deny every domain",
    [POINTERS.mcpAllow]: "Allow no MCP servers",
    [POINTERS.marketplaces]: "Block every marketplace",
});

const sameMember = (rule) => (rule === `${RULE}GITHUB_LOGIN` ? (a, b) => a.toLowerCase() === b.toLowerCase() : (a, b) => a === b);

function memberProblem(text, rule) {
    if (rule === `${RULE}GITHUB_LOGIN` && !GITHUB_LOGIN.test(text)) return "Use an organization login: letters, digits and single hyphens, up to 39 characters.";
    return "";
}

function EmptyListAction({ pointer, value, onChange, disabled }) {
    const label = EMPTY_LIST_ACTIONS[pointer];
    if (!label || disabled) return null;
    return <button type="button" className={`${buttonClass} justify-self-start`} disabled={Array.isArray(value) && value.length === 0} onClick={() => onChange([])}>{label}</button>;
}

function TextValue({ value, onChange, disabled, label, pointer }) {
    const problem = pointer === POINTERS.model && value !== undefined && !String(value).trim() ? "Enter auto or a model ID." : "";
    return <div className="grid gap-[5px]">
        <input className={`${inputClass} w-full`} aria-label={label} placeholder={PLACEHOLDERS[pointer]} value={value ?? ""} disabled={disabled} onChange={(event) => onChange(event.target.value || undefined)} />
        {problem ? <span className={NOTE_CLASS}>{problem}</span> : null}
    </div>;
}

function BoolValue({ value, onChange, disabled, label, pointer }) {
    const details = BOOL_DETAILS[pointer] ?? {};
    return <div className="grid gap-[6px] sm:grid-cols-3" role="radiogroup" aria-label={label}>
        {[{ value: undefined, label: "Not configured", description: details.unset }, { value: true, label: "On", description: details.on }, { value: false, label: "Off", description: details.off }].map((option) =>
            <ChoiceCard key={String(option.value)} role="radio" label={option.label} description={option.description} status={value === option.value ? "Selected" : "Not selected"} selected={value === option.value} disabled={disabled} onClick={() => onChange(option.value)} />)}
    </div>;
}

function EnumValue({ value, onChange, disabled, label, options, pointer }) {
    const choices = [undefined, ...options];
    if (value && !options.includes(value)) choices.push(value);
    const details = CHOICE_DETAILS[pointer] ?? {};
    return <div className="grid gap-[6px] sm:grid-cols-2" role="radiogroup" aria-label={label}>
        {choices.map((option) => <ChoiceCard key={option ?? "unset"} role="radio" label={option === undefined ? "Not configured" : optionLabel(option)} description={option === undefined ? "No value is imposed by this policy." : details[option]} status={value === option ? "Selected" : "Not selected"} selected={value === option} disabled={disabled} onClick={() => onChange(option)} />)}
    </div>;
}

/// A list of strings. Members are checked against the catalog's rule and,
/// where the catalog asks for it, against each other.
export function StringList({ value, onChange, disabled, label, rule, distinct, placeholder, pointer }) {
    const [draft, setDraft] = useState("");
    const items = Array.isArray(value) ? value : [];
    const same = sameMember(rule);
    const text = draft.trim();
    const duplicate = distinct && text && items.some((item) => same(item, text));
    const problem = text ? memberProblem(text, rule) : "";
    const add = () => { if (text && !duplicate && !problem) { onChange([...items, text]); setDraft(""); } };
    const remove = (index) => { const next = items.filter((_, position) => position !== index); onChange(next.length ? next : EMPTY_LIST_ACTIONS[pointer] ? [] : undefined); };
    return <div className="grid gap-[7px]">
        {items.length ? <div className="grid border-t border-[color:var(--line)]">
            {items.map((item, index) => <div key={index} className="grid min-h-[36px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[8px] border-b border-[color:var(--line)]">
                <code className="min-w-0 break-all text-[11px]">{item}</code>
                {!disabled ? <button className={removeClass} type="button" aria-label={`Remove ${item}`} onClick={() => remove(index)}>Remove</button> : null}
            </div>)}
        </div> : null}
        {!disabled ? <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-[6px]">
            <input className={inputClass} aria-label={`New ${label.toLowerCase()} entry`} placeholder={placeholder} value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); add(); } }} />
            <button className={buttonClass} type="button" disabled={!text || duplicate || Boolean(problem)} onClick={add}>Add</button>
        </div> : null}
        {duplicate ? <span className={NOTE_CLASS}>This entry is already in the list.</span> : null}
        {problem ? <span className={NOTE_CLASS}>{problem}</span> : null}
    </div>;
}

/// Part of `strictPluginOnlyCustomization`: one card per kind in `members`.
/// The value is `true` (every kind), a list of kinds, or `false` / unset
/// (none); a change writes the locked kinds as a list, in catalog order.
function SurfaceLock({ value, onChange, disabled, label, entry, members }) {
    const surfaces = entry.allowedValues;
    const locked = value === true ? surfaces : Array.isArray(value) ? value : [];
    const toggle = (surface) => {
        const next = surfaces.filter((item) => (item === surface ? !locked.includes(item) : locked.includes(item)));
        onChange(next.length ? next : undefined);
    };
    return <div className="grid gap-[6px] md:grid-cols-2" role="group" aria-label={label}>
        {members.map((surface) => {
            const on = locked.includes(surface);
            return <ChoiceCard key={surface} role="checkbox" label={SURFACE_LABELS[surface] ?? optionLabel(surface)} description={on ? "Only plugins and managed settings may add these." : "People and repositories may add these."} status={on ? "Locked" : "Not locked"} selected={on} disabled={disabled} onClick={() => toggle(surface)} />;
        })}
    </div>;
}

/// `autoTier`: a bare tier name locks it, `{ overridable }` sets a default.
function AutoTier({ value, onChange, disabled, label }) {
    // A written value decides what is shown. The draft only holds a choice
    // that cannot be written yet: a mode with no tier name, or a name with a space.
    const [draftMode, setDraftMode] = useState("unset");
    const [draftName, setDraftName] = useState("");
    const writtenMode = typeof value === "string" ? "locked" : value && typeof value === "object" ? "default" : null;
    const mode = writtenMode ?? draftMode;
    const name = writtenMode ? (typeof value === "string" ? value : value.overridable ?? "") : draftName;
    const setMode = (next) => { setDraftMode(next); setDraftName(name); };
    const setName = (next) => { setDraftMode(mode); setDraftName(next); };
    const write = (nextMode, nextName) => {
        const tier = nextName.trim();
        if (nextMode === "unset" || !tier || /\s/.test(tier)) onChange(undefined);
        else onChange(nextMode === "locked" ? tier : { overridable: tier });
    };
    const modes = [
        { id: "unset", label: "Not configured", description: "Nothing is written. Auto uses the person's own tier." },
        { id: "locked", label: "Locked tier", description: "People and repositories cannot change the tier." },
        { id: "default", label: "Default tier", description: "A starting tier that people and repositories can change." },
    ];
    return <div className="grid gap-[8px]">
        <div className="grid gap-[6px] sm:grid-cols-3" role="radiogroup" aria-label={label}>
            {modes.map((option) => <ChoiceCard key={option.id} role="radio" label={option.label} description={option.description} status={mode === option.id ? "Selected" : "Not selected"} selected={mode === option.id} disabled={disabled} onClick={() => { setMode(option.id); write(option.id, name); }} />)}
        </div>
        {mode !== "unset" ? <input className={inputClass} aria-label="Tier name" placeholder="Tier name" value={name} disabled={disabled} onChange={(event) => { setName(event.target.value); write(mode, event.target.value); }} /> : null}
        {mode !== "unset" && !name.trim() ? <span className={NOTE_CLASS}>Enter a tier name. Nothing is written until you do.</span> : null}
        {/\s/.test(name.trim()) ? <span className={NOTE_CLASS}>A tier name cannot contain spaces.</span> : null}
    </div>;
}

/// `remoteControl`: a mode, and for requireSSO the organizations and domains
/// that satisfy it.
function RemoteControl({ value, onChange, disabled, label, entry }) {
    const current = value && typeof value === "object" ? value : {};
    const details = CHOICE_DETAILS[POINTERS.remoteControl];
    const setMode = (mode) => {
        if (mode === undefined) return onChange(undefined);
        if (mode !== "requireSSO") return onChange({ mode });
        return onChange({ ...current, mode });
    };
    const setList = (key, list) => {
        const next = { ...current, [key]: list };
        if (list === undefined) delete next[key];
        onChange(next);
    };
    return <div className="grid gap-[8px]">
        <div className="grid gap-[6px] sm:grid-cols-2" role="radiogroup" aria-label={label}>
            {[undefined, ...entry.allowedValues].map((mode) => <ChoiceCard key={mode ?? "unset"} role="radio" label={mode === undefined ? "Not configured" : mode === "requireSSO" ? "Require SSO" : optionLabel(mode)} description={mode === undefined ? "Nothing is written. Remote control follows the person's own setting." : details[mode]} status={current.mode === mode ? "Selected" : "Not selected"} selected={current.mode === mode} disabled={disabled} onClick={() => setMode(mode)} />)}
        </div>
        {current.mode === "requireSSO" ? <div className="grid gap-[10px] border-t border-[color:var(--line)] pt-[8px]">
            <p className={`${NOTE_CLASS} m-0`}>Add at least one organization or domain. An account must be authorized in one of them.</p>
            <div className="grid gap-[5px]"><span className="text-[11px] font-medium">GitHub.com organizations</span>
                <StringList label="GitHub.com organizations" value={current.githubDotComOrganizations} onChange={(list) => setList("githubDotComOrganizations", list)} disabled={disabled} rule={`${RULE}GITHUB_LOGIN`} distinct placeholder="octocorp" /></div>
            <div className="grid gap-[5px]"><span className="text-[11px] font-medium">GitHub Enterprise Cloud domains</span>
                <StringList label="GitHub Enterprise Cloud domains" value={current.githubEnterpriseCloudDomains} onChange={(list) => setList("githubEnterpriseCloudDomains", list)} disabled={disabled} distinct placeholder="octocorp.ghe.com" /></div>
        </div> : null}
    </div>;
}

const MATCHER_KINDS = Object.freeze([
    ["serverUrl", "Remote server (URL)"],
    ["serverCommand", "Local server (command)"],
    ["serverName", "Server name"],
]);

/// `allowedMcpServers` and `deniedMcpServers`: single-field matchers.
function McpMatchers({ value, onChange, disabled, pointer, entry }) {
    const [kind, setKind] = useState("serverUrl");
    const [target, setTarget] = useState("");
    const [args, setArgs] = useState("");
    const items = Array.isArray(value) ? value : [];
    const strictName = entry.elementRule === `${RULE}MCP_SERVER_NAME`;
    const text = target.trim();
    const nameProblem = kind === "serverName" && strictName && text && !MCP_SERVER_NAME.test(text) ? "A server name in the allowlist may use only letters, digits, _ and -." : "";
    const matcher = kind === "serverCommand"
        ? { serverCommand: [text, ...args.split("\n").map((line) => line.trim()).filter(Boolean)] }
        : { [kind]: text };
    const add = () => { if (text && !nameProblem) { onChange([...items, matcher]); setTarget(""); setArgs(""); } };
    const remove = (index) => { const next = items.filter((_, position) => position !== index); onChange(next.length ? next : EMPTY_LIST_ACTIONS[pointer] ? [] : undefined); };
    return <div className="grid gap-[8px]">
        {items.map((item, index) => <div key={index} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[8px] border border-[color:var(--line)] p-[8px] text-[11px]">
            <span>{describeMcpMatcher(item)}</span>
            {!disabled ? <button type="button" className={buttonClass} onClick={() => remove(index)}>Remove</button> : null}
        </div>)}
        {Array.isArray(value) && !value.length ? <p className={`${NOTE_CLASS} m-0`}>The list is empty on purpose. No MCP server may load except built-in GitHub servers.</p> : null}
        {!disabled ? <div className="grid gap-[6px] border-t border-[color:var(--line)] pt-[9px]">
            <span className="text-[11px] font-medium">Add a server</span>
            <select className={inputClass} aria-label="Server match type" value={kind} onChange={(event) => setKind(event.target.value)}>{MATCHER_KINDS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select>
            <input className={inputClass} aria-label={kind === "serverUrl" ? "Server URL" : kind === "serverCommand" ? "Server executable" : "Server name"} placeholder={kind === "serverUrl" ? "https://mcp.example.com/*" : kind === "serverCommand" ? "npx" : "github"} value={target} onChange={(event) => setTarget(event.target.value)} />
            {kind === "serverCommand" ? <textarea className={`${inputClass} min-h-[60px] py-[6px]`} aria-label="Server arguments" placeholder={"Arguments, one per line\n-y\n@example/mcp-server"} value={args} onChange={(event) => setArgs(event.target.value)} /> : null}
            {kind === "serverUrl" ? <span className={NOTE_CLASS}>A * matches any text.</span> : null}
            {kind === "serverCommand" ? <span className={NOTE_CLASS}>The command and every argument must match exactly, in order.</span> : null}
            {nameProblem ? <span className={NOTE_CLASS}>{nameProblem}</span> : null}
            <div className="flex flex-wrap gap-[6px]">
                <button type="button" className={buttonClass} disabled={!text || Boolean(nameProblem)} onClick={add}>Add server</button>
                <EmptyListAction pointer={pointer} value={value} onChange={onChange} disabled={disabled} />
            </div>
        </div> : null}
    </div>;
}

const FIELD_LABELS = Object.freeze({
    repo: "Repository (OWNER/REPO)",
    ref: "Ref",
    path: "Path",
    url: "URL",
    package: "Package",
    hostPattern: "Host pattern",
    pathPattern: "Path pattern",
});

const shapeFields = (shape) => (shape?.fields ?? []).map((field) => ({
    name: field.name,
    required: Boolean(field.required),
    constant: field.constant ?? "",
    rule: field.stringRule ?? field.string_rule ?? "",
}));

/// `strictKnownMarketplaces`: source descriptors, each discriminated by its
/// constant `source` field.
function MarketplaceSources({ value, onChange, disabled, pointer, entry }) {
    const shapes = (entry.objectShapes ?? []).map(shapeFields).map((fields) => ({
        kind: fields.find((field) => field.name === "source")?.constant ?? "",
        fields: fields.filter((field) => field.name !== "source"),
    })).filter((shape) => shape.kind);
    const [kind, setKind] = useState(shapes[0]?.kind ?? "");
    const [fields, setFields] = useState({});
    const items = Array.isArray(value) ? value : [];
    const shape = shapes.find((item) => item.kind === kind);
    const ownerRepoProblem = shape?.fields.some((field) => field.rule === `${RULE}OWNER_REPO` && fields[field.name]?.trim() && !/^[^/:]+\/[^/:]+$/.test(fields[field.name].trim()));
    const ready = shape && shape.fields.every((field) => !field.required || fields[field.name]?.trim()) && !ownerRepoProblem;
    const add = () => {
        if (!ready) return;
        const source = { source: kind };
        for (const field of shape.fields) if (fields[field.name]?.trim()) source[field.name] = fields[field.name].trim();
        onChange([...items, source]);
        setFields({});
    };
    const remove = (index) => { const next = items.filter((_, position) => position !== index); onChange(next.length ? next : EMPTY_LIST_ACTIONS[pointer] ? [] : undefined); };
    return <div className="grid gap-[8px]">
        {items.map((item, index) => <div key={index} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[8px] border border-[color:var(--line)] p-[8px] text-[11px]">
            <span>{describeMarketplaceSource(item)}</span>
            {!disabled ? <button type="button" className={buttonClass} onClick={() => remove(index)}>Remove</button> : null}
        </div>)}
        {Array.isArray(value) && !value.length ? <p className={`${NOTE_CLASS} m-0`}>The list is empty on purpose. No marketplace may be added.</p> : null}
        {!disabled && shapes.length ? <div className="grid gap-[6px] border-t border-[color:var(--line)] pt-[9px]">
            <span className="text-[11px] font-medium">Add a source</span>
            <select className={inputClass} aria-label="Source type" value={kind} onChange={(event) => { setKind(event.target.value); setFields({}); }}>{shapes.map((item) => <option key={item.kind} value={item.kind}>{sourceKindLabel(item.kind)}</option>)}</select>
            {shape?.fields.map((field) => <input key={field.name} className={inputClass} aria-label={FIELD_LABELS[field.name] ?? field.name} placeholder={`${FIELD_LABELS[field.name] ?? field.name}${field.required ? "" : " (optional)"}`} value={fields[field.name] ?? ""} onChange={(event) => setFields({ ...fields, [field.name]: event.target.value })} />)}
            {ownerRepoProblem ? <span className={NOTE_CLASS}>Use OWNER/REPO, for example octocorp/plugins.</span> : null}
            <div className="flex flex-wrap gap-[6px]">
                <button type="button" className={buttonClass} disabled={!ready} onClick={add}>Add source</button>
                <EmptyListAction pointer={pointer} value={value} onChange={onChange} disabled={disabled} />
            </div>
        </div> : null}
    </div>;
}


const fieldId = (pointer) => `copilot-field-${pointer.slice(1).replaceAll("/", "-")}`;

function CopilotField({ pointer, controls, updateControls, catalog, canManage }) {
    const entry = catalog?.settings?.find((setting) => setting.pointer === pointer);
    const [label, help] = COPILOT_COPY[pointer];
    const value = valueAt(controls.document, pointer);
    const onChange = (next) => updateControls((old) => ({ ...old, document: setValueAt(old.document, pointer, next) }));
    const header = <div className="grid gap-[3px]">
        <div className="flex flex-wrap items-center gap-[8px]">
            <h3 className="m-0 text-[12px] font-medium text-[color:var(--ink)]">{label}</h3>
        </div>
        {help ? <p className={`${NOTE_CLASS} m-0`}>{help}</p> : null}
    </div>;
    if (!entry) return <div id={fieldId(pointer)} className="grid gap-[4px] py-[8px]">{header}<p className={`${NOTE_CLASS} m-0`}>This control is unavailable in the current server catalog.</p></div>;
    const kind = entry.valueKind;
    const disabled = !canManage;
    const common = { label, value, onChange, disabled, pointer, entry };
    let control = null;
    if (kind === `${KIND}BOOL`) control = <BoolValue {...common} />;
    else if (kind === `${KIND}STRING`) control = <TextValue {...common} />;
    else if (kind === `${KIND}ENUM`) control = <EnumValue {...common} options={entry.allowedValues} />;
    else if (kind === `${KIND}STRING_ARRAY`) control = <div className="grid gap-[7px]"><StringList {...common} rule={entry.elementRule} distinct={entry.distinctMembers} placeholder={PLACEHOLDERS[pointer]} /><EmptyListAction {...common} /></div>;
    else if (kind === `${KIND}AUTO_TIER`) control = <AutoTier {...common} />;
    else if (kind === `${KIND}REMOTE_CONTROL`) control = <RemoteControl {...common} />;
    else if (kind === `${KIND}MCP_SERVER_MATCHERS`) control = <McpMatchers {...common} />;
    else if (kind === `${KIND}OBJECT_ARRAY`) control = <MarketplaceSources {...common} />;
    const endpoints = onEndpoints(pointer, controls.document);
    const collisions = interactions(pointer, controls.document);
    return <div id={fieldId(pointer)} className="grid gap-[7px] border-t border-[color:var(--line)] pt-[12px] first:border-0 first:pt-0">
        {header}
        <InputHelp pointer={pointer} />
        {control}
        {endpoints ? <OnEndpoints written={endpoints.written}>{endpoints.text}</OnEndpoints> : null}
        {collisions.map((item) => <Interaction key={item.target} scope={item.scope}>{item.text}</Interaction>)}
        {EMPTY_LIST_ACTIONS[pointer] && value !== undefined ? <button type="button" className={`${buttonClass} justify-self-start`} disabled={!canManage} onClick={() => onChange(undefined)}>Stop managing this restriction</button> : null}
        <FieldFooter pointer={pointer} document={controls.document} />
    </div>;
}

// A shared-array spec names the members this subsection owns.
const specPointer = (spec) => (typeof spec === "string" ? spec : spec.pointer);
const specKey = (spec) => {
    const key = specPointer(spec).slice(1).replaceAll("/", ".");
    if (typeof spec === "string") return key;
    // Claude Code's chips: the member in brackets, or the bare key for the rest.
    return spec.members ? `${key}[${spec.members.join(", ")}]` : key;
};

/// The kinds of `strictPluginOnlyCustomization` one subsection owns.
function SurfaceField({ spec, controls, updateControls, catalog, canManage }) {
    const pointer = spec.pointer;
    const entry = catalog?.settings?.find((setting) => setting.pointer === pointer);
    const part = spec.members?.includes("mcp") ? "mcp" : "content";
    const [label, help] = SURFACE_COPY[part];
    const value = valueAt(controls.document, pointer);
    const onChange = (next) => updateControls((old) => ({ ...old, document: setValueAt(old.document, pointer, next) }));
    const members = entry ? entry.allowedValues.filter((item) => (spec.members ? spec.members.includes(item) : !spec.exceptMembers.includes(item))) : [];
    const endpoints = surfaceOnEndpoints(members, controls.document);
    return <div id={fieldId(`${pointer}/${part}`)} className="grid gap-[7px] border-t border-[color:var(--line)] pt-[12px] first:border-0 first:pt-0">
        <div className="grid gap-[3px]">
            <div className="flex flex-wrap items-center gap-[8px]">
                <h3 className="m-0 text-[12px] font-medium text-[color:var(--ink)]">{label}</h3>
            </div>
            {help ? <p className={`${NOTE_CLASS} m-0`}>{help}</p> : null}
        </div>

        {entry ? <SurfaceLock label={label} value={value} onChange={onChange} disabled={!canManage} entry={entry} members={members} /> : <p className={`${NOTE_CLASS} m-0`}>This control is unavailable in the current server catalog.</p>}
        <OnEndpoints written={endpoints.written}>{endpoints.text}</OnEndpoints>
    </div>;
}

export function CopilotSection({ title, pointers, controls, updateControls, catalog, canManage }) {
    return <><GroupHeader title={title} keys={pointers.map(specKey)} />{pointers.map((spec) => (typeof spec === "string"
        ? <CopilotField key={spec} pointer={spec} controls={controls} updateControls={updateControls} catalog={catalog} canManage={canManage} />
        : <SurfaceField key={specKey(spec)} spec={spec} controls={controls} updateControls={updateControls} catalog={catalog} canManage={canManage} />))}</>;
}
